// Time formatting. Site times use the site's IANA zone via Intl.DateTimeFormat;
// operator-local times use the browser zone.

export const OPERATOR_TZ: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

const cache = new Map<string, Intl.DateTimeFormat>();

function validZone(tz: string | null | undefined): string {
  if (!tz) return 'UTC';
  const key = `valid|${tz}`;
  if (cache.has(key)) return tz;
  try {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz });
    cache.set(key, f);
    return tz;
  } catch {
    return 'UTC';
  }
}

function dtf(tz: string, opts: Intl.DateTimeFormatOptions, tag: string): Intl.DateTimeFormat {
  const key = `${tag}|${tz}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts });
    cache.set(key, f);
  }
  return f;
}

function toDate(v: string | number | Date): Date | null {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Short zone name (MST, EDT, GMT+2…) for a zone at a given instant. */
export function tzAbbr(tz: string | null | undefined, at: string | number | Date = Date.now()): string {
  const zone = validZone(tz);
  const d = toDate(at) ?? new Date();
  const part = dtf(zone, { timeZoneName: 'short' }, 'abbr')
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? zone;
}

/** "Oct 15 02:14:05" in the given zone. */
export function fmtDateTime(v: string | number | Date | null | undefined, tz: string | null | undefined, withZone = true): string {
  if (v === null || v === undefined) return '—';
  const d = toDate(v);
  if (!d) return String(v);
  const zone = validZone(tz);
  const s = dtf(
    zone,
    { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' },
    'dt',
  ).format(d);
  return withZone ? `${s} ${tzAbbr(zone, d)}` : s;
}

/** "02:14:05" in the given zone. */
export function fmtTime(v: string | number | Date, tz: string | null | undefined, seconds = true): string {
  const d = toDate(v);
  if (!d) return String(v);
  const zone = validZone(tz);
  return dtf(
    zone,
    { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}), hourCycle: 'h23' },
    seconds ? 'ts' : 'tm',
  ).format(d);
}

/** Full date for clocks and overlays: "2026-10-15". */
export function fmtDate(v: string | number | Date, tz: string | null | undefined): string {
  const d = toDate(v);
  if (!d) return String(v);
  const zone = validZone(tz);
  const parts = dtf(zone, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'date').formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Site-local time, plus the operator-local equivalent for a tooltip. */
export function siteAndLocal(v: string | null | undefined, siteTz: string | null | undefined): { site: string; local: string } {
  if (!v) return { site: '—', local: '' };
  return {
    site: fmtDateTime(v, siteTz ?? 'UTC'),
    local: `Your time: ${fmtDateTime(v, OPERATOR_TZ)} · UTC ${fmtDateTime(v, 'UTC', false)}`,
  };
}

/** Operator-local time for admin tables. */
export function fmtLocal(v: string | null | undefined): string {
  return v ? fmtDateTime(v, OPERATOR_TZ) : '—';
}

export function relAge(v: string | null | undefined, now = Date.now()): string {
  if (!v) return '';
  const d = toDate(v);
  if (!d) return '';
  const s = Math.round((now - d.getTime()) / 1000);
  if (s < 0) return 'in the future';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
